import { useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";

const courseOptions = [
  { id: "ion-anti-aging", label: "離子紅光逆齡抗衰老 課程" },
  { id: "ion-stretch-mark", label: "離子紅光去妊娠紋課程" },
  { id: "youthful-eyelid", label: "童顏眼瞼課程" },
  { id: "ai-postpartum", label: "高階全科產後課程" },
];

const ENROLL_POINTS = 100;

function levelFromPoints(points: number): string {
  if (points >= 500) return "金級會員";
  if (points >= 200) return "銀級會員";
  return "普通會員";
}

export default function EnrollmentForm() {
  const { user, profile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const formRef = useRef<HTMLFormElement>(null);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const honeypot = (data.get("company_alt") as string) || "";
    if (honeypot.trim().length > 0) {
      setStatus("success");
      form.reset();
      return;
    }
    data.delete("company_alt");
    const email = (data.get("email") as string) || "";
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setErrorMsg("請輸入有效的電子郵件地址");
      return;
    }
    setStatus("loading");
    try {
      const params = new URLSearchParams();
      data.forEach((v, k) => params.append(k, String(v)));
      const res = await fetch("https://readdy.ai/api/form/dadu4hhtto0ceqqngmg0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const text = await res.text();
      let parsed: any = null;
      try { parsed = JSON.parse(text); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";
      const isSpam = /spam/i.test(serverMsg) || /spam/i.test(text);
      if (res.ok && parsed?.code === "OK" && !isSpam) {
        // 會員登入時，同步寫入報名紀錄並發放點數
        let pointsAwarded = 0;
        if (user) {
          const selectedLabel = (data.get("course") as string) || "";
          const selectedCourse = courseOptions.find((c) => c.label === selectedLabel);
          const courseId = selectedCourse?.id || "";
          const courseTitle = selectedCourse?.label || selectedLabel;

          await supabase.from("enrollments").insert({
            user_id: user.id,
            course_id: courseId,
            course_title: courseTitle,
            status: "pending",
            points_awarded: ENROLL_POINTS,
          });

          const currentPoints = profile?.points ?? 0;
          const newPoints = currentPoints + ENROLL_POINTS;
          await supabase
            .from("profiles")
            .update({ points: newPoints, level: levelFromPoints(newPoints), updated_at: new Date().toISOString() })
            .eq("id", user.id);
          pointsAwarded = ENROLL_POINTS;
          await refreshProfile();
        }

        setStatus("success");
        setSuccessMsg(
          user
            ? `報名成功！已為你記錄報名並累積 ${pointsAwarded} 點會員點數。`
            : "報名成功！我們的顧問會盡快與你聯絡，感謝你選擇 ISHB。"
        );
        form.reset();
      } else {
        setStatus("error");
        setErrorMsg(serverMsg || "報名失敗，請稍後再試");
      }
    } catch {
      setStatus("error");
      setErrorMsg("網路錯誤，請稍後再試");
    }
  };

  return (
    <section id="enroll" className="py-16 md:py-20 bg-white">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <div className="inline-block px-6 py-2 rounded-md bg-brand-600 text-white text-lg tracking-wider">課程報名</div>
          <h2 className="mt-5 font-serif text-3xl md:text-4xl text-brand-800">預約報讀課程</h2>
          <p className="mt-4 text-brand-700/70 text-sm md:text-base">
            填妥以下資料，我們的專業顧問會盡快與你聯絡，為你安排最合適的課程。
          </p>
          {user ? (
            <p className="mt-3 inline-flex items-center gap-1.5 text-sm text-brand-600">
              <i className="ri-user-star-line"></i> 以會員身份報名，可額外累積 {ENROLL_POINTS} 點點數
            </p>
          ) : (
            <p className="mt-3 text-sm text-brand-700/60">
              想累積會員點數？{" "}
              <button
                onClick={() => navigate("/auth?redirect=/courses")}
                className="text-brand-600 font-medium cursor-pointer"
              >
                先登入 / 註冊會員
              </button>
            </p>
          )}
        </div>

        <form
          id="enrollment-form"
          ref={formRef}
          onSubmit={onSubmit}
          data-readdy-form
          className="bg-brand-50/60 border border-brand-100 rounded-lg p-6 md:p-8 space-y-5"
        >
          <input
            type="text"
            name="company_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
            className="hp-honeypot"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label htmlFor="enroll-name" className="block text-sm font-medium text-brand-900 mb-1.5">姓名 *</label>
              <input
                id="enroll-name"
                type="text"
                name="name"
                required
                placeholder="你的姓名"
                className="w-full rounded-md border border-brand-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
              />
            </div>
            <div>
              <label htmlFor="enroll-phone" className="block text-sm font-medium text-brand-900 mb-1.5">聯絡電話 *</label>
              <input
                id="enroll-phone"
                type="tel"
                name="phone"
                required
                placeholder="你的電話號碼"
                className="w-full rounded-md border border-brand-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
              />
            </div>
          </div>

          <div>
            <label htmlFor="enroll-email" className="block text-sm font-medium text-brand-900 mb-1.5">電郵地址 *</label>
            <input
              id="enroll-email"
              type="email"
              name="email"
              required
              placeholder="your@email.com"
              className="w-full rounded-md border border-brand-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
            />
          </div>

          <div>
            <label htmlFor="enroll-course" className="block text-sm font-medium text-brand-900 mb-1.5">想報讀的課程 *</label>
            <select
              id="enroll-course"
              name="course"
              required
              defaultValue=""
              className="w-full rounded-md border border-brand-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
            >
              <option value="" disabled>請選擇課程</option>
              {courseOptions.map((c) => (
                <option key={c.id} value={c.label}>{c.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="enroll-message" className="block text-sm font-medium text-brand-900 mb-1.5">留言（選填）</label>
            <textarea
              id="enroll-message"
              name="message"
              rows={4}
              maxLength={500}
              placeholder="如有特別需求或想了解更多，歡迎在此留言（最多 500 字）"
              className="w-full rounded-md border border-brand-200 bg-white px-4 py-2.5 text-sm resize-none focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
            ></textarea>
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={status === "loading"}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-brand-600 hover:bg-brand-700 text-white text-sm whitespace-nowrap cursor-pointer transition disabled:opacity-60"
            >
              {status === "loading" ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i> 送出中...
                </>
              ) : (
                <>
                  提交報名 <i className="ri-arrow-right-line"></i>
                </>
              )}
            </button>

            {status === "success" && (
              <p className="mt-3 text-sm text-brand-600 text-center">{successMsg}</p>
            )}
            {status === "error" && (
              <p className="mt-3 text-sm text-rose-500 text-center">{errorMsg}</p>
            )}
          </div>
        </form>
      </div>
    </section>
  );
}